@if (isLoading) {
  <div class="loader-overlay" role="status" aria-live="polite">
    <div class="heart-wrapper" aria-hidden="true">
      <svg class="heart-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path
          class="heart-shape"
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
               11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        />
        <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
      </svg>
    </div>
    <p class="loader-text mt-3 fw-semibold text-dark">Loading appointments...</p>
  </div>
}

@if (!isLoading) {
  <app-portal-navigation
    [portalName]="portalName"
    [portalIcon]="portalIcon"
    [portalClass]="portalClass"
    collapseId="bookingsMenu"
    [firstName]="userProfile?.firstName ?? ''"
    [lastName]="userProfile?.lastName ?? ''"
    [primaryItems]="primaryNavigationItems"
    [profileRoute]="profileRoute"
    [additionalItems]="additionalNavigationItems"
  >
  </app-portal-navigation>

  <div class="booking-list-page container-fluid p-3 p-md-4">
    <!-- Hero Banner matching booking-view -->
    <div class="booking-hero card border-0 mb-4">
      <div class="booking-hero__content">
        <div>
          <div class="booking-hero__eyebrow">Practice Schedule & Consultations</div>
          <h2 class="booking-hero__title mb-2">Appointments Management</h2>
          <div class="booking-hero__meta">
            <span
              ><i class="bi bi-calendar-check me-1 text-primary"></i>Manage and track all patient
              visits</span
            >
            <span
              ><i class="bi bi-person-badge me-1"></i>{{ userProfile?.firstName }}
              {{ userProfile?.lastName }}</span
            >
          </div>
        </div>

        <div class="booking-hero__status">
          <span
            class="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-2"
          >
            {{
              completedBookings.length +
                ongoingBookings.length +
                pendingBookings.length +
                noShowBookings.length
            }}
            Total Appointments
          </span>
        </div>
      </div>
    </div>

    <div class="card booking-card">
      <div class="card-header booking-card__header pb-0 border-bottom-0">
        <!-- Modern Navigation Tabs -->
        <ul class="nav nav-tabs border-bottom-0 gap-2" id="bookingTabs" role="tablist">
          <li class="nav-item">
            <button
              class="nav-link active"
              data-bs-toggle="tab"
              data-bs-target="#completed"
              role="tab"
            >
              <i class="bi bi-check-circle me-1 text-success"></i>Completed
              <span
                class="badge bg-success-subtle text-success border border-success-subtle rounded-pill ms-1"
              >
                {{ completedBookings.length }}
              </span>
            </button>
          </li>

          <li class="nav-item">
            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#ongoing" role="tab">
              <i class="bi bi-play-circle me-1 text-primary"></i>Confirmed & Ongoing
              <span
                class="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill ms-1"
              >
                {{ ongoingBookings.length }}
              </span>
            </button>
          </li>

          <li class="nav-item">
            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#pending" role="tab">
              <i class="bi bi-clock me-1 text-danger"></i>Pending
              <span
                class="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill ms-1"
              >
                {{ pendingBookings.length }}
              </span>
            </button>
          </li>

          <li class="nav-item">
            <button class="nav-link" data-bs-toggle="tab" data-bs-target="#noshow" role="tab">
              <i class="bi bi-person-slash me-1 text-warning"></i>No Show
              <span
                class="badge bg-warning-subtle text-warning border border-warning-subtle rounded-pill ms-1"
              >
                {{ noShowBookings.length }}
              </span>
            </button>
          </li>
        </ul>
      </div>

      <div class="card-body p-4">
        <div class="tab-content">
          <!-- Completed Tab -->
          <div class="tab-pane fade show active" id="completed" role="tabpanel">
            @if (completedBookings.length > 0) {
              <div class="d-flex flex-column gap-3">
                @for (booking of paginatedCompletedBookings; track booking.id) {
                  <app-booking-card
                    [booking]="booking"
                    [statusClass]="getStatusClass(booking.status)"
                    (view)="viewDetails($event)"
                    (cancel)="cancelBooking($event)"
                  >
                  </app-booking-card>
                }
              </div>

              <!-- Pagination -->
              @if (completedPageSize > 1) {
                <div class="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                  <small class="text-muted"
                    >Page {{ completedCurrentPage }} of {{ completedPageSize }}</small
                  >
                  <div class="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="completedCurrentPage === 1"
                      (click)="completedPreviousPage()"
                    >
                      <i class="bi bi-chevron-left"></i> Previous
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="completedCurrentPage === completedPageSize"
                      (click)="completedNextPage()"
                    >
                      Next <i class="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              }
            } @else {
              <div class="empty-state">
                <i class="bi bi-calendar-check fs-1 d-block mb-2 text-primary"></i>
                <h5>No Completed Appointments</h5>
                <p class="text-muted small">Completed consultation visits will appear here.</p>
              </div>
            }
          </div>

          <!-- Ongoing Tab -->
          <div class="tab-pane fade" id="ongoing" role="tabpanel">
            @if (ongoingBookings.length > 0) {
              <div class="d-flex flex-column gap-3">
                @for (booking of paginatedOngoingBookings; track booking.id) {
                  <app-booking-card
                    [booking]="booking"
                    [statusClass]="getStatusClass(booking.status)"
                    [canComplete]="canCompleteBooking(booking)"
                    (view)="viewDetails($event)"
                    (start)="startBooking($event)"
                    (complete)="completeBooking($event)"
                    (noShow)="noShowBooking($event)"
                    (cancel)="cancelBooking($event)"
                  >
                  </app-booking-card>
                }
              </div>

              <!-- Pagination -->
              @if (ongoingPageSize > 1) {
                <div class="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                  <small class="text-muted"
                    >Page {{ ongoingCurrentPage }} of {{ ongoingPageSize }}</small
                  >
                  <div class="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="ongoingCurrentPage === 1"
                      (click)="ongoingPreviousPage()"
                    >
                      <i class="bi bi-chevron-left"></i> Previous
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="ongoingCurrentPage === ongoingPageSize"
                      (click)="ongoingNextPage()"
                    >
                      Next <i class="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              }
            } @else {
              <div class="empty-state">
                <i class="bi bi-calendar-event fs-1 d-block mb-2 text-primary"></i>
                <h5>No Active Appointments</h5>
                <p class="text-muted small">
                  Confirmed and in-progress patient visits will be listed here.
                </p>
              </div>
            }
          </div>

          <!-- Pending Tab -->
          <div class="tab-pane fade" id="pending" role="tabpanel">
            @if (pendingBookings.length > 0) {
              <div class="d-flex flex-column gap-3">
                @for (booking of paginatedPendingBookings; track booking.id) {
                  <app-booking-card
                    [booking]="booking"
                    [statusClass]="getStatusClass(booking.status)"
                    (view)="viewDetails($event)"
                    (noShow)="noShowBooking($event)"
                    (cancel)="cancelBooking($event)"
                  >
                  </app-booking-card>
                }
              </div>

              <!-- Pagination -->
              @if (pendingPageSize > 1) {
                <div class="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                  <small class="text-muted"
                    >Page {{ pendingCurrentPage }} of {{ pendingPageSize }}</small
                  >
                  <div class="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="pendingCurrentPage === 1"
                      (click)="pendingPreviousPage()"
                    >
                      <i class="bi bi-chevron-left"></i> Previous
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="pendingCurrentPage === pendingPageSize"
                      (click)="pendingNextPage()"
                    >
                      Next <i class="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              }
            } @else {
              <div class="empty-state">
                <i class="bi bi-hourglass fs-1 d-block mb-2 text-primary"></i>
                <h5>No Pending Appointments</h5>
                <p class="text-muted small">No unconfirmed bookings waiting for review.</p>
              </div>
            }
          </div>

          <!-- No Show Tab -->
          <div class="tab-pane fade" id="noshow" role="tabpanel">
            @if (noShowBookings.length > 0) {
              <div class="d-flex flex-column gap-3">
                @for (booking of paginatedNoShowBookings; track booking.id) {
                  <app-booking-card
                    [booking]="booking"
                    [statusClass]="getStatusClass(booking.status)"
                    (view)="viewDetails($event)"
                  >
                  </app-booking-card>
                }
              </div>

              <!-- Pagination -->
              @if (noShowPageSize > 1) {
                <div class="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
                  <small class="text-muted"
                    >Page {{ noShowCurrentPage }} of {{ noShowPageSize }}</small
                  >
                  <div class="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="noShowCurrentPage === 1"
                      (click)="noShowPreviousPage()"
                    >
                      <i class="bi bi-chevron-left"></i> Previous
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      [disabled]="noShowCurrentPage === noShowPageSize"
                      (click)="noShowNextPage()"
                    >
                      Next <i class="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              }
            } @else {
              <div class="empty-state">
                <i class="bi bi-person-slash fs-1 d-block mb-2 text-warning"></i>
                <h5>No No-Show Appointments</h5>
                <p class="text-muted small">
                  Appointments where the patient did not attend will appear here.
                </p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
}
